import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export const emptyBannerAppConfig = {
  enabled: true,
  appOnly: true,
  imageUrl: '',
  headline: '',
  headlineEn: '',
  subtitle: '',
  subtitleEn: '',
  showAd: true,
  contentTone: 'auto',
  sourceIcon: 'none',
  fullCreative: true,
};

export default function BannerAppSettings({ value, onChange, file, onFileChange, fallbackImage }) {
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [previewLocale, setPreviewLocale] = useState('ko');
  useEffect(() => {
    if (!file) {
      setPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const change = (key, next) =>
    onChange({
      ...value,
      [key]: next,
      ...(value.feedPostId && ['headline', 'headlineEn', 'subtitle', 'subtitleEn'].includes(key)
        ? { feedCopyMode: 'manual' }
        : {}),
    });
  const image = preview || value.imageUrl || fallbackImage;
  const headline = previewLocale === 'en' ? value.headlineEn || value.headline : value.headline;
  const subtitle = previewLocale === 'en' ? value.subtitleEn || value.subtitle : value.subtitle;
  return (
    <Stack spacing={2}>
      <Typography variant="h6">앱 홈 상단 롤링 이미지</Typography>
      <Alert severity="info">
        앱 이미지는 14:9, 권장 1400×900px입니다. 앱 전용 이미지가 없으면 위의 대표 이미지를
        사용합니다. 앱 문구는 이미지 안에 표시됩니다.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
      <FormControlLabel
        label="앱 홈에 공개"
        control={
          <Checkbox checked={value.enabled} onChange={(e) => change('enabled', e.target.checked)} />
        }
      />
      <FormControlLabel
        label="앱에만 표시 (기존 웹 배너에서는 숨김)"
        control={
          <Checkbox checked={value.appOnly} onChange={(e) => change('appOnly', e.target.checked)} />
        }
      />
      {value.feedPostId && (
        <Alert severity="info">
          피드 연동 배너는 ‘앱에만 표시’를 해제하면 선택한 기기(PC/모바일)의 웹 배너에도 표시됩니다.
          웹은 기존 가로형 배너 크기를 유지하며, 선택한 이미지와 제목 1줄·설명 최대 2줄을
          표시합니다.
        </Alert>
      )}
      <FormControlLabel
        label="AD 표시"
        control={
          <Checkbox checked={value.showAd} onChange={(e) => change('showAd', e.target.checked)} />
        }
      />
      <Button component="label" variant="outlined">
        앱 이미지 선택
        <input
          hidden
          aria-label="앱 홈 이미지 파일"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => {
            const next = e.target.files?.[0];
            e.target.value = '';
            if (!next) return;
            if (
              !['image/png', 'image/jpeg', 'image/webp'].includes(next.type) ||
              next.size > 5 * 1024 * 1024
            ) {
              setError('PNG/JPG/WebP 5MB 이하 이미지를 선택해주세요.');
              return;
            }
            setError('');
            onFileChange(next);
          }}
        />
      </Button>
      <Button
        onClick={() => {
          onFileChange(null);
          change('imageUrl', '');
        }}
      >
        앱 전용 이미지 해제
      </Button>
      {['headline', 'headlineEn', 'subtitle', 'subtitleEn'].map((key, i) => (
        <TextField
          key={key}
          label={
            ['앱 제목 (선택)', '앱 영문 제목 (선택)', '앱 설명 (선택)', '앱 영문 설명 (선택)'][i]
          }
          value={value[key]}
          inputProps={{ maxLength: i < 2 ? 160 : 300 }}
          onChange={(e) => change(key, e.target.value)}
        />
      ))}
      <TextField
        select
        label="앱 문구 색"
        value={value.contentTone}
        onChange={(e) => change('contentTone', e.target.value)}
      >
        <MenuItem value="auto">이미지에 따라 자동</MenuItem>
        <MenuItem value="light">흰색</MenuItem>
        <MenuItem value="dark">검정</MenuItem>
      </TextField>
      <TextField
        select
        label="앱 제목 아이콘"
        value={value.sourceIcon}
        onChange={(e) => change('sourceIcon', e.target.value)}
      >
        <MenuItem value="none">없음</MenuItem>
        <MenuItem value="youtube">YouTube</MenuItem>
      </TextField>
      <TextField
        select
        label="미리보기 언어"
        value={previewLocale}
        onChange={(e) => setPreviewLocale(e.target.value)}
      >
        <MenuItem value="ko">한국어</MenuItem>
        <MenuItem value="en">English</MenuItem>
      </TextField>
      {image && (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 353,
            aspectRatio: '14 / 9',
            borderRadius: '18px',
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            alt="앱 홈 배너 미리보기"
            src={image}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {(headline || subtitle) && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                px: 2,
                py: 2,
                color: value.contentTone === 'dark' ? '#111' : '#fff',
                background:
                  value.contentTone === 'dark'
                    ? 'linear-gradient(transparent, rgba(255,255,255,.7))'
                    : 'linear-gradient(transparent, rgba(0,0,0,.7))',
              }}
            >
              <Typography noWrap fontWeight={600} sx={{ color: 'inherit' }}>
                {value.sourceIcon === 'youtube' ? '▶ ' : ''}
                {headline}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'inherit',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                }}
              >
                {subtitle}
              </Typography>
            </Box>
          )}
          {value.showAd && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,.5)',
                color: '#fff',
                px: 0.5,
                fontSize: 9,
              }}
            >
              AD
            </Box>
          )}
        </Box>
      )}
      <Typography variant="caption">
        미리보기는 문구 위치·이미지 잘림 확인용입니다. 자동 색상·네이티브 블러의 최종 모습은 앱에서
        확인하세요. 제목 1줄·설명 2줄까지 표시됩니다.
      </Typography>
    </Stack>
  );
}
